import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlMatch } from '../models/control-state';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-control-match-card',
  imports: [CommonModule],
  templateUrl: './control-match-card.html',
  styleUrl: './control-match-card.scss',
})
export class ControlMatchCardComponent {
  @Input() match!: ControlMatch;
  @Input() position = 0;
  @Input() variant: 'en-cancha' | 'siguiente' | 'espera' | 'finalizado' = 'espera';
  @Input() isAdmin = false;
  @Input() isRecentlyMoved = false;

  @Output() startMatch = new EventEmitter<ControlMatch>();
  @Output() showQr = new EventEmitter<ControlMatch>();
  @Output() setOrder = new EventEmitter<ControlMatch>();

  // Configurable por environment: oculta la hora programada del partido
  // (badge "h:mm AM/PM") en todas las variantes.
  readonly showScheduledTime = environment.showMatchScheduledTime !== false;

  get player1Name(): string {
    return this.match.player1?.playerName || this.match.player1Id || '?';
  }

  get player2Name(): string {
    return this.match.player2?.playerName || this.match.player2Id || '?';
  }

  get currentScore(): string {
    if (!this.match.games || this.match.games.length === 0) return '';
    const lastGame = this.match.games[this.match.games.length - 1];
    return `${lastGame.player1Points || 0}-${lastGame.player2Points || 0}`;
  }

  get gamesScore(): string {
    return `${this.match.player1GamesWon || 0}-${this.match.player2GamesWon || 0}`;
  }

  get currentGameNumber(): number {
    return (this.match.player1GamesWon || 0) + (this.match.player2GamesWon || 0) + 1;
  }

  get scheduledTime(): string {
    const date = this.match.date;
    if (!date) return '';
    let d: Date;
    if (date instanceof Date) {
      d = date;
    } else if (typeof (date as any).toDate === 'function') {
      d = (date as any).toDate();
    } else if (typeof (date as any).seconds === 'number') {
      d = new Date((date as any).seconds * 1000);
    } else {
      return '';
    }
    const hours = d.getHours();
    const minutes = d.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const h = hours % 12 || 12;
    return `${h}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  }

  get isOverdue(): boolean {
    const date = this.match.date;
    if (!date) return false;
    let millis: number;
    if (date instanceof Date) {
      millis = date.getTime();
    } else if (typeof (date as any).toMillis === 'function') {
      millis = (date as any).toMillis();
    } else if (typeof (date as any).seconds === 'number') {
      millis = (date as any).seconds * 1000;
    } else {
      return false;
    }
    return Date.now() > millis;
  }

  get priorityLabel(): string {
    if (!this.match.playerAvailable) return 'Bloqueado';
    if (this.match.priority <= -40) return 'Alta';
    if (this.match.priority <= -10) return 'Media';
    return 'Normal';
  }

  get priorityClass(): string {
    if (!this.match.playerAvailable) return 'blocked';
    if (this.match.priority <= -40) return 'high';
    if (this.match.priority <= -10) return 'medium';
    return 'normal';
  }

  get matchScores(): string[] {
    if (!this.match.games) return [];
    return this.match.games
      .filter(g => (g.player1Points || 0) > 0 || (g.player2Points || 0) > 0)
      .map(g => `${g.player1Points}-${g.player2Points}`);
  }

  get isWinner1(): boolean {
    return this.match.playerIdWinner === this.match.player1Id;
  }

  get isWinner2(): boolean {
    return this.match.playerIdWinner === this.match.player2Id;
  }

  onStartMatch(): void {
    this.startMatch.emit(this.match);
  }

  onShowQr(): void {
    this.showQr.emit(this.match);
  }

  onSetOrder(): void {
    this.setOrder.emit(this.match);
  }
}
